import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { getGatewayUrl, getApiKey } from '@spcc/core';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Download,
  FileText,
  AlertTriangle,
  CheckCircle,
} from '@spcc/uikit';

export const Route = createFileRoute('/d/$token')({
  component: DriverOrderPage,
});

type DriverOrderSummary = {
  orderNumber: string | null;
  orderStatus: string | null;
  deliveryDate: string | null;
  deliveryLocation: string | null;
  plant: string | null;
  product: {
    name: string | null;
    variant: string | null;
  };
  quantity: number | string | null;
  driver: { name: string | null } | null;
  vehicle: { licensePlate: string | null; type: string | null } | null;
};

type FetchState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: DriverOrderSummary }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

function buildInfoUrl(token: string): string {
  return `${getGatewayUrl()}/api/fulfillment/public/d/${encodeURIComponent(token)}`;
}

function buildPdfUrl(token: string): string {
  return `${getGatewayUrl()}/api/fulfillment/public/d/${encodeURIComponent(token)}/pdf`;
}

function buildApiHeaders(): Record<string, string> {
  const apiKey = getApiKey();
  return apiKey ? { 'X-API-Key': apiKey } : {};
}

async function fetchOrderSummary(token: string): Promise<FetchState> {
  try {
    const response = await fetch(buildInfoUrl(token), {
      headers: buildApiHeaders(),
    });

    if (response.status === 404) return { kind: 'not_found' };
    if (response.status === 410) return { kind: 'expired' };
    if (!response.ok) {
      return {
        kind: 'error',
        message: `Request failed (${response.status})`,
      };
    }

    const body = (await response.json()) as { data?: DriverOrderSummary };
    if (!body?.data) {
      return { kind: 'error', message: 'Malformed response from server' };
    }
    return { kind: 'ok', data: body.data };
  } catch (err) {
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

function sanitizeFilename(value: string): string {
  // Strip path separators / characters that are invalid on common filesystems.
  return value.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'order';
}

async function downloadPdfViaBlob(
  token: string,
  orderNumber: string | null,
): Promise<{ ok: true } | { ok: false; status?: number; message: string }> {
  const response = await fetch(buildPdfUrl(token), { headers: buildApiHeaders() });
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      message: `PDF download failed (${response.status})`,
    };
  }

  const blob = await response.blob();
  // Kong's CORS config doesn't expose `Content-Disposition` to the browser, so
  // we build the filename from the orderNumber we already fetched. Fall back to
  // the token only if orderNumber is missing.
  const base = orderNumber?.trim() ? orderNumber.trim() : `order-${token}`;
  const filename = `${sanitizeFilename(base)}.pdf`;

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Release the object URL after a short tick so the browser can start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  return { ok: true };
}

function formatQuantity(value: number | string | null): string {
  if (value == null) return 'N/A';
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString();
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-800 py-3 last:border-b-0">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-base text-slate-100 whitespace-pre-line">{value?.trim() || 'N/A'}</span>
    </div>
  );
}

function ErrorView({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6">
      <Card className="w-full max-w-md border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-400" />
            <CardTitle className="text-xl text-slate-100">{title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-300">{description}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function LoadingView() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-slate-200" />
        <p className="text-lg font-medium text-slate-200">Loading order details...</p>
      </div>
    </div>
  );
}

function OrderView({
  token,
  data,
  downloadState,
  onManualDownload,
}: {
  token: string;
  data: DriverOrderSummary;
  downloadState: 'idle' | 'pending' | 'done' | 'failed';
  onManualDownload: () => void;
}) {
  const isFailed = downloadState === 'failed';
  return (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-8">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <div
          className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 ${
            isFailed
              ? 'bg-red-950/50 text-red-300 border border-red-900/60'
              : 'bg-emerald-950/50 text-emerald-300 border border-emerald-900/60'
          }`}
        >
          <CheckCircle className="h-5 w-5" />
          <span className="text-sm font-medium">
            {isFailed
              ? 'Automatic download failed. Use the button below to retry.'
              : 'Your download should start automatically. If nothing happens, use the button below.'}
          </span>
        </div>

        <Card className="border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-sky-400" />
              <div className="flex flex-col">
                <CardTitle className="text-xl text-slate-100">Order Details</CardTitle>
                {data.orderNumber && (
                  <span className="text-sm text-slate-400">#{data.orderNumber}</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <DetailRow label="Delivery Location" value={data.deliveryLocation} />
            <DetailRow label="Plant" value={data.plant} />
            <DetailRow
              label="Product"
              value={
                [data.product?.name, data.product?.variant]
                  .filter(Boolean)
                  .join(' - ') || null
              }
            />
            <DetailRow label="Quantity" value={formatQuantity(data.quantity)} />
            <DetailRow label="Driver" value={data.driver?.name ?? null} />
            <DetailRow
              label="Vehicle"
              value={
                data.vehicle
                  ? [data.vehicle.licensePlate, data.vehicle.type]
                      .filter(Boolean)
                      .join(' - ') || null
                  : null
              }
            />
          </CardContent>
        </Card>

        <button
          type="button"
          onClick={onManualDownload}
          disabled={downloadState === 'pending'}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-6 py-3 font-medium text-white shadow-lg transition-colors hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
          data-token={token}
        >
          <Download className="h-5 w-5" />
          {downloadState === 'pending' ? 'Preparing PDF...' : 'Download PDF'}
        </button>
      </div>
    </div>
  );
}

function DriverOrderPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [downloadState, setDownloadState] = useState<
    'idle' | 'pending' | 'done' | 'failed'
  >('idle');
  const downloadTriggeredRef = useRef(false);

  const runDownload = async (orderNumber: string | null) => {
    setDownloadState('pending');
    const result = await downloadPdfViaBlob(token, orderNumber);
    if (result.ok) {
      setDownloadState('done');
    } else {
      setDownloadState('failed');
      // If the PDF endpoint says the token expired/consumed after grace, reflect that.
      if (result.status === 410) {
        setState({ kind: 'expired' });
      } else if (result.status === 404) {
        setState({ kind: 'not_found' });
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchOrderSummary(token);
      if (cancelled) return;
      setState(result);

      if (result.kind === 'ok' && !downloadTriggeredRef.current) {
        downloadTriggeredRef.current = true;
        const orderNumber = result.data.orderNumber;
        // Slight delay so the page renders first; keeps UX calm.
        setTimeout(() => void runDownload(orderNumber), 300);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state.kind === 'loading') return <LoadingView />;
  if (state.kind === 'not_found') {
    return (
      <ErrorView
        title="Link not found"
        description="This link is invalid. Please check the URL or contact support."
      />
    );
  }
  if (state.kind === 'expired') {
    return (
      <ErrorView
        title="Link expired"
        description="This link has expired or has already been used. Please request a new one."
      />
    );
  }
  if (state.kind === 'error') {
    return <ErrorView title="Something went wrong" description={state.message} />;
  }

  return (
    <OrderView
      token={token}
      data={state.data}
      downloadState={downloadState}
      onManualDownload={() => void runDownload(state.data.orderNumber)}
    />
  );
}
